import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-global';
const USE_MOCK_DATA = false;

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const defaultForm: CreateShiftPassdownInput = {
    category: 'GLOBAL',
    notableCases: '',
    bolosPEs: '',
    oldestRespondCases: '',
    oldestReviewCases: '',
    escalationsNextSteps: '',
    issuesBlockers: '',
    nextForgeAudits: '',
    csStaffPresent: 0, csStaffRequired: 0,
    csirStaffPresent: 0, csirStaffRequired: 0,
    priorityAlarmsStaffPresent: 0, priorityAlarmsStaffRequired: 0,
    nonPriorityAlarmsStaffPresent: 0, nonPriorityAlarmsStaffRequired: 0,
    lsocStaffPresent: 0, lsocStaffRequired: 0,
};

const GlobalPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<CreateShiftPassdownInput>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof CreateShiftPassdownInput, value: string | number | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.gsbpassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('GLOBAL');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'GLOBAL',
            notableCases: b.notableCases || '',
            bolosPEs: b.bolosPEs || '',
            oldestRespondCases: b.oldestRespondCases || '',
            oldestReviewCases: b.oldestReviewCases || '',
            escalationsNextSteps: b.escalationsNextSteps || '',
            issuesBlockers: b.issuesBlockers || '',
            nextForgeAudits: b.nextForgeAudits || '',
            notableCasesJson: b.notableCasesJson,
            bolosPEsJson: b.bolosPEsJson,
            oldestRespondCasesJson: b.oldestRespondCasesJson,
            oldestReviewCasesJson: b.oldestReviewCasesJson,
            escalationsNextStepsJson: b.escalationsNextStepsJson,
            issuesBlockersJson: b.issuesBlockersJson,
            nextForgeAuditsJson: b.nextForgeAuditsJson,
            csStaffPresent: b.csStaffPresent || 0, csStaffRequired: b.csStaffRequired || 0,
            csirStaffPresent: b.csirStaffPresent || 0, csirStaffRequired: b.csirStaffRequired || 0,
            priorityAlarmsStaffPresent: b.priorityAlarmsStaffPresent || 0, priorityAlarmsStaffRequired: b.priorityAlarmsStaffRequired || 0,
            nonPriorityAlarmsStaffPresent: b.nonPriorityAlarmsStaffPresent || 0, nonPriorityAlarmsStaffRequired: b.nonPriorityAlarmsStaffRequired || 0,
            lsocStaffPresent: b.lsocStaffPresent || 0, lsocStaffRequired: b.lsocStaffRequired || 0,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, form); }
            else { await createShiftPassdown(form); }
            resetForm();
            setSuccessMsg(isUpdate ? 'Passdown updated successfully' : 'Passdown created successfully');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // Render a Global rich-text field read-only via the shared static renderer
    // (no dangerouslySetInnerHTML). Prefers the Tiptap JSON when present; for
    // legacy records that only have a plain-text string, formatPlainTextToHtml
    // preserves line breaks/links before RichTextContent parses it with the
    // read-only schema — same approach as Announcements.
    const renderRichField = (label: string, json?: Record<string, any>, value?: string, exactCase = false) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                {/* exactCase disables the SCSS uppercase transform for labels whose
                    casing must be preserved verbatim (e.g. "BOLOs/PEs"). */}
                <h3 style={exactCase ? { textTransform: 'none' } : undefined}>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    const renderStaffRow = (label: string, present?: number, required?: number) => {
        const pre = present ?? 0;
        const req = required ?? 0;
        const pct = req > 0 ? Math.round((pre / req) * 100) : 0;
        return (
            <tr>
                <td>{label}</td>
                <td>{pre}</td>
                <td>{req}</td>
                <td className={pct >= 100 ? 'staffing-pct--good' : pct >= 75 ? 'staffing-pct--warn' : 'staffing-pct--low'}>
                    {req > 0 ? `${pct}%` : '-'}
                </td>
            </tr>
        );
    };

    const maxStaff = 50;
    const b0 = passdowns[0];
    const hasStaffingError = [form.csStaffPresent, form.csStaffRequired, form.csirStaffPresent, form.csirStaffRequired, form.priorityAlarmsStaffPresent, form.priorityAlarmsStaffRequired, form.nonPriorityAlarmsStaffPresent, form.nonPriorityAlarmsStaffRequired, form.lsocStaffPresent, form.lsocStaffRequired].some(val => Number(val) > 50);
    const hasContent = [
        form.notableCases, form.bolosPEs, form.oldestRespondCases,
        form.oldestReviewCases, form.escalationsNextSteps, form.issuesBlockers, form.nextForgeAudits,
    ].every(val => !!richTextToPlain(val));

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg && 
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label={<>Notable Cases{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            autoFocus
                            value={form.notableCasesJson ?? formatPlainTextToHtml(form.notableCases || '')}
                            placeholder="Notable cases..."
                            onChangeText={(text) => updateField('notableCases', text)}
                            onChangeJson={(json) => updateField('notableCasesJson', json)}
                        />
                    </FormField>

                    <FormField label={<>BOLOs/PEs{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.bolosPEsJson ?? formatPlainTextToHtml(form.bolosPEs || '')}
                            placeholder="BOLOs and PEs..."
                            onChangeText={(text) => updateField('bolosPEs', text)}
                            onChangeJson={(json) => updateField('bolosPEsJson', json)}
                        />
                    </FormField>
                    
                    <FormField label={<>Three Oldest Cases in "Respond" and Path to Closure{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.oldestRespondCasesJson ?? formatPlainTextToHtml(form.oldestRespondCases || '')}
                            placeholder="Three oldest cases in Respond..."
                            onChangeText={(text) => updateField('oldestRespondCases', text)}
                            onChangeJson={(json) => updateField('oldestRespondCasesJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Three Oldest Cases in "Review" and Path to Closure{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.oldestReviewCasesJson ?? formatPlainTextToHtml(form.oldestReviewCases || '')}
                            placeholder="Three oldest cases in Review..."
                            onChangeText={(text) => updateField('oldestReviewCases', text)}
                            onChangeJson={(json) => updateField('oldestReviewCasesJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Escalations and Resolutions/Next Steps{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.escalationsNextStepsJson ?? formatPlainTextToHtml(form.escalationsNextSteps || '')}
                            placeholder="Escalations and resolutions..."
                            onChangeText={(text) => updateField('escalationsNextSteps', text)}
                            onChangeJson={(json) => updateField('escalationsNextStepsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Issues/Blockers{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.issuesBlockersJson ?? formatPlainTextToHtml(form.issuesBlockers || '')}
                            placeholder="Issues or blockers..."
                            onChangeText={(text) => updateField('issuesBlockers', text)}
                            onChangeJson={(json) => updateField('issuesBlockersJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Next Three FORGE Audits (Global){requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.nextForgeAuditsJson ?? formatPlainTextToHtml(form.nextForgeAudits || '')}
                            placeholder="Upcoming FORGE audits..."
                            onChangeText={(text) => updateField('nextForgeAudits', text)}
                            onChangeJson={(json) => updateField('nextForgeAuditsJson', json)}
                        />
                    </FormField>

                    <FormField label="STAFFING" stretch></FormField>
                    <div className="passdown-form__staffing-grid">
                        <div className="passdown-form__staffing-row passdown-form__staffing-row--header">
                            <span className="passdown-form__staffing-col-header">TEAM</span>
                            <span className="passdown-form__staffing-col-header">Present</span>
                            <span className="passdown-form__staffing-col-header">Required</span>
                        </div>
                        {[
                            { label: 'CS', pKey: 'csStaffPresent' as const, rKey: 'csStaffRequired' as const },
                            { label: 'CSIR', pKey: 'csirStaffPresent' as const, rKey: 'csirStaffRequired' as const },
                            { label: 'Priority Alarms', pKey: 'priorityAlarmsStaffPresent' as const, rKey: 'priorityAlarmsStaffRequired' as const },
                            { label: 'Non-Priority Alarms', pKey: 'nonPriorityAlarmsStaffPresent' as const, rKey: 'nonPriorityAlarmsStaffRequired' as const },
                            { label: 'LSOC', pKey: 'lsocStaffPresent' as const, rKey: 'lsocStaffRequired' as const },
                        ].map(({ label, pKey, rKey }) => (
                            <div key={label} className="passdown-form__staffing-row">
                                <span className="passdown-form__staffing-label">{label}</span>
                                <Input 
                                    inputMode="numeric"
                                    type="number"
                                    invalid={Number(form[pKey]) > maxStaff}
                                    value={String(form[pKey] || 0)}
                                    onChange={({ detail }) => updateField(pKey, Math.max(0, parseInt(detail.value) || 0))} 
                                />
                                <Input 
                                    inputMode="numeric"
                                    type="number"
                                    invalid={Number(form[rKey]) > maxStaff}
                                    value={String(form[rKey] || 0)}
                                    onChange={({ detail }) => updateField(rKey, Math.max(0, parseInt(detail.value) || 0))} 
                                />
                            </div>
                        ))}
                    </div>

                    <div className="passdown-form__actions">
                        {requiredFieldHint()}
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || hasStaffingError || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update Global Passdown' : 'Submit Global Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No global passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('globalShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.notableCases) || richTextToPlain(b0.escalationsNextSteps)} ...</p>}

                            {activeExpanded && (
                                <>
                                    <div className="passdown-entry__fields-grid">
                                        {renderRichField('Notable Cases', b0.notableCasesJson, b0.notableCases)}
                                        {renderRichField('BOLOs/PEs', b0.bolosPEsJson, b0.bolosPEs, true)}
                                        {renderRichField('Three Oldest Cases in "Respond"', b0.oldestRespondCasesJson, b0.oldestRespondCases)}
                                        {renderRichField('Three Oldest Cases in "Review"', b0.oldestReviewCasesJson, b0.oldestReviewCases)}
                                        {renderRichField('Escalations and Resolutions/Next Steps', b0.escalationsNextStepsJson, b0.escalationsNextSteps)}
                                        {renderRichField('Issues/Blockers', b0.issuesBlockersJson, b0.issuesBlockers)}
                                        {renderRichField('Next three FORGE Audits (Global)', b0.nextForgeAuditsJson, b0.nextForgeAudits)}
                                    </div>
                                    <div className="passdown-entry__field">
                                        <div className="passdown-staffing-header">
                                            <h4>STAFFING</h4>
                                        </div>
                                        <table className="passdown-staffing-table">
                                            <thead><tr><th>Team</th><th>Present</th><th>Required</th><th>%</th></tr></thead>
                                            <tbody>
                                                {renderStaffRow('CS', b0.csStaffPresent, b0.csStaffRequired)}
                                                {renderStaffRow('CSIR', b0.csirStaffPresent, b0.csirStaffRequired)}
                                                {renderStaffRow('Priority Alarms', b0.priorityAlarmsStaffPresent, b0.priorityAlarmsStaffRequired)}
                                                {renderStaffRow('Non-Priority Alarms', b0.nonPriorityAlarmsStaffPresent, b0.nonPriorityAlarmsStaffRequired)}
                                                {renderStaffRow('LSOC', b0.lsocStaffPresent, b0.lsocStaffRequired)}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 GLOBAL PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((gsb, idx) => {
                                    const isExpanded = expandedId === gsb.passdownId;
                                    return (
                                        <span key={gsb.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                    <div className="passdown-history__content">
                                                        <div className="passdown-history__top">
                                                            <span className="passdown-history__date">
                                                                <FormattedDate date={gsb.createdAt} /> &middot; by {gsb.createdBy}
                                                                { gsb.updatedAt && gsb.updatedAt !== gsb.createdAt && (
                                                                    <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={gsb.updatedAt} /> &middot; by {gsb.updatedBy}</span>
                                                                )}
                                                            </span>
                                                            <div className="passdown-history__top-right">
                                                                {canModify('globalShiftPassdown', gsb.createdBy) && (
                                                                    <ActionButtons
                                                                        onEdit={() => handleEdit(gsb)}
                                                                        onDelete={() => handleDeleteClick(gsb)}
                                                                        disabled={showForm}
                                                                    />
                                                                )}
                                                                <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : gsb.passdownId)}>
                                                                    <Icon
                                                                        name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                        variant="subtle"
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="passdown-history__details">
                                                                <div className="passdown-entry__fields-grid">
                                                                    {renderRichField('Notable Cases', gsb.notableCasesJson, gsb.notableCases)}
                                                                    {renderRichField('BOLOs/PEs', gsb.bolosPEsJson, gsb.bolosPEs, true)}
                                                                    {renderRichField('Three Oldest Cases in "Respond"', gsb.oldestRespondCasesJson, gsb.oldestRespondCases)}
                                                                    {renderRichField('Three Oldest Cases in "Review"', gsb.oldestReviewCasesJson, gsb.oldestReviewCases)}
                                                                    {renderRichField('Escalations and Resolutions/Next Steps', gsb.escalationsNextStepsJson, gsb.escalationsNextSteps)}
                                                                    {renderRichField('Issues/Blockers', gsb.issuesBlockersJson, gsb.issuesBlockers)}
                                                                    {renderRichField('Next three FORGE Audits (Global)', gsb.nextForgeAuditsJson, gsb.nextForgeAudits)}
                                                                </div>
                                                                <div className="passdown-entry__field">
                                                                    <div className="passdown-staffing-header">
                                                                        <h4>STAFFING</h4>
                                                                    </div>
                                                                    <table className="passdown-staffing-table">
                                                                        <thead><tr><th>Team</th><th>Present</th><th>Required</th><th>%</th></tr></thead>
                                                                        <tbody>
                                                                            {renderStaffRow('CS', gsb.csStaffPresent, gsb.csStaffRequired)}
                                                                            {renderStaffRow('CSIR', gsb.csirStaffPresent, gsb.csirStaffRequired)}
                                                                            {renderStaffRow('Priority Alarms', gsb.priorityAlarmsStaffPresent, gsb.priorityAlarmsStaffRequired)}
                                                                            {renderStaffRow('Non-Priority Alarms', gsb.nonPriorityAlarmsStaffPresent, gsb.nonPriorityAlarmsStaffRequired)}
                                                                            {renderStaffRow('LSOC', gsb.lsocStaffPresent, gsb.lsocStaffRequired)}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button 
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        // className="section-card__view-more" 
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                 <Modal 
                    visible 
                    header="Delete Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                        </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default GlobalPassdown;
